import React, { useMemo, useState } from "react"
import { Modal, Input } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCopy } from "@fortawesome/free-solid-svg-icons"

import { UnmappedColumnsPayload } from "types/dataImport.types"

interface UnmappedColumnsModalProps {
  open: boolean
  payload: UnmappedColumnsPayload
  onClose: () => void
}

/**
 * A browsable review of every data-file column that was not imported.
 *
 * An import can leave eighty or more columns behind, so this is a searchable list rather than
 * a sentence. Columns whose name contains a variable that IS in the datamap come first: those
 * are the decorated twins a scripting platform emits, and they usually mean the datamap is
 * wrong rather than that the column is platform noise.
 *
 * @component
 */
const UnmappedColumnsModal: React.FC<UnmappedColumnsModalProps> = ({
  open,
  payload,
  onClose,
}) => {
  const [search, setSearch] = useState("")

  const query = search.trim().toLowerCase()

  const related = useMemo(
    () =>
      payload.related.filter(
        (entry) =>
          query === "" ||
          entry.column.toLowerCase().includes(query) ||
          entry.relatedTo.toLowerCase().includes(query),
      ),
    [payload.related, query],
  )

  const other = useMemo(
    () => payload.other.filter((name) => query === "" || name.toLowerCase().includes(query)),
    [payload.other, query],
  )

  const copy = (names: string[]): void => {
    void navigator.clipboard?.writeText(names.join("\n"))
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      // centered + an explicit body container keep the dialog put. Without them it is
      // positioned against the nearest positioned ancestor and drifts as the page scrolls.
      centered
      getContainer={() => document.body}
      // No cap on the body: the two group boxes are already bounded, so capping here only
      // added a third scrollbar (and a stray horizontal one, since overflow-y:auto makes
      // overflow-x resolve to auto too).
      title={`${payload.count} columns in the data file were not imported`}
    >
      <p className="text-sm text-slate-500 mt-0 mb-3">
        Every name below is a <strong>column in your data file</strong> that has no entry in
        the datamap, so its data was discarded. Scripting platforms always add some of their
        own — the ones grouped first are the ones worth checking.
      </p>

      <Input.Search
        allowClear
        placeholder="Search column names"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-4"
      />

      {related.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-slate-800 m-0">
              Likely related to a datamap variable ({related.length})
            </h4>
            <button
              onClick={() => copy(related.map((entry) => entry.column))}
              aria-label="Copy related column names"
              className="text-slate-500 hover:text-slate-800 transition-colors p-1"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          </div>
          <div className="border border-slate-200 rounded overflow-hidden">
            <div className="grid grid-cols-2 gap-4 px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wide">
              <span>Data file column</span>
              <span>Datamap variable it contains</span>
            </div>
            <div className="max-h-40 overflow-y-auto overflow-x-hidden [scrollbar-width:thin]">
              {related.map((entry) => (
                <div
                  key={entry.column}
                  className="grid grid-cols-2 gap-4 px-3 py-1.5 text-sm border-b border-slate-100 last:border-b-0"
                >
                  {/* min-w-0 so a long name wraps inside its cell instead of widening the
                      grid and pushing a horizontal scrollbar onto the container. */}
                  <span className="font-mono text-slate-800 break-all min-w-0">{entry.column}</span>
                  <span className="font-mono text-slate-500 break-all min-w-0">{entry.relatedTo}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {other.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-slate-800 m-0">
              Other columns ({other.length})
            </h4>
            <button
              onClick={() => copy(other)}
              aria-label="Copy other column names"
              className="text-slate-500 hover:text-slate-800 transition-colors p-1"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          </div>
          <div className="border border-slate-200 rounded overflow-hidden">
            <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wide">
              Data file column
            </div>
            <div className="max-h-40 overflow-y-auto overflow-x-hidden [scrollbar-width:thin]">
              {other.map((name) => (
                <div
                  key={name}
                  className="px-3 py-1.5 text-sm font-mono text-slate-700 border-b border-slate-100 last:border-b-0 break-all"
                >
                  {name}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {related.length === 0 && other.length === 0 && (
        <p className="text-sm text-slate-500">No column names match “{search}”.</p>
      )}
    </Modal>
  )
}

export default UnmappedColumnsModal
